import React from "react";
import { Box, Grid, Paper, Avatar, Typography, Divider } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import AwardDetailViewModal from "./Leaderboard/AwardDetailViewModal";
import Compliance from "./Leaderboard/Compliance";
import Leaders from "./Leaderboard/Leaders";
import LeadersList from "./Leaderboard/LeadersList";
import AwardButton from "../../components/Award/AwardButton";
import { Tooltip } from "../../components/Tooltip";

const Title = () => {
  const { t } = useTranslation();
  return (
    <Typography variant="body2" color="initial">
      {t("components.tooltip.LeaderboardAverageScore")}
    </Typography>
  );
};

const leaderboardGroupTypes = {
  location: "location",
  company: "company",
  position: "position"
};
const awardContainerSize = {
  xs: 12,
  sm: 6,
  md: 3,
  xl: 3
};

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    backgroundColor: "white",
    minHeight: "calc(100vh - 286px)"
  },
  bigAvatar: {
    margin: theme.spacing(3),
    width: theme.spacing(8),
    height: theme.spacing(8)
  }
}));

const LeadersModal = ({ open, onClose, leaders, value }) => {
  return (
    <Modal
      maxWidth="sm"
      fullWidth
      dialogTitle={
        <Typography variant="h4" color="textPrimary">
          {`${value} Award Leaders`}
        </Typography>
      }
      dialogContent={leaders && <LeadersList leaders={leaders} />}
      open={open}
      onClose={onClose}
      showCloseIcon
      scrollable
    />
  );
};

export default function Leaderboard(props) {
  const classes = useStyles();
  const { awardLeadersData, user, availableAwards } = props;

  const [groupLeadersModalOpen, setGroupLeadersModalOpen] = React.useState(
    false
  );
  const [leadersModalInfo, setLeadersModalInfo] = React.useState(null);
  const [
    availableAwardModalAward,
    setAvailableAwardModalAward
  ] = React.useState(null);
  const [availableAwardModalOpen, setAvailableAwardModalOpen] = React.useState(
    false
  );

  const handleOpenGroupLeadersModal = groupType => {
    setGroupLeadersModalOpen(true);
    const value =
      groupType === leaderboardGroupTypes.location
        ? user.location
        : groupType === leaderboardGroupTypes.company
        ? user.companyName
        : user[groupType]; // groupType can be one of location, city, position

    setLeadersModalInfo({
      leaders: awardLeadersData[groupType].leaders,
      value
    });
  };

  const handleCloseGroupLeadersModal = () => {
    setGroupLeadersModalOpen(false);
  };

  const handleOpenAvailableAwardModal = awardName => {
    setAvailableAwardModalOpen(true);
    setAvailableAwardModalAward(awardName);
  };

  const handleCloseAvailableAwardModal = () => {
    setAvailableAwardModalOpen(false);
  };

  return (
    <Paper data-qa="performance-leaderboard" className={classes.container}>
      <Box display="flex" alignItems="center">
        <Avatar
          alt={`${user.firstName} ${user.lastName}`}
          src={user.avatarUrl}
          className={classes.bigAvatar}
        />
        <Typography variant="h3">{`${user.firstName} ${user.lastName}`}</Typography>
      </Box>
      <Divider />
      <Box m={2.5}>
        <Grid container spacing={2}>
          <Grid item {...awardContainerSize} key={0}>
            <Compliance
              compliancePercent={user.compliancePercent}
              completedTraining={user.completedCourses}
              totalTrainingHours={user.totalTrainingHours}
              courseAvg={user.averageScore}
            />
          </Grid>
          {awardLeadersData.position && (
            <Tooltip title={<Title />} placement="bottom">
              <Grid item {...awardContainerSize}>
                <Leaders
                  groupType={leaderboardGroupTypes.position}
                  groupTypeValue={user.position}
                  averageScore={awardLeadersData.position.averageScore}
                  leaders={awardLeadersData.position.leaders}
                  onViewAllClicked={() =>
                    handleOpenGroupLeadersModal(leaderboardGroupTypes.position)
                  }
                />
              </Grid>
            </Tooltip>
          )}
          {awardLeadersData.location && (
            <Tooltip title={<Title />} placement="bottom">
              <Grid item {...awardContainerSize}>
                <Leaders
                  groupType={leaderboardGroupTypes.location}
                  groupTypeValue={user.location}
                  averageScore={awardLeadersData.location.averageScore}
                  leaders={awardLeadersData.location.leaders}
                  onViewAllClicked={() =>
                    handleOpenGroupLeadersModal(leaderboardGroupTypes.location)
                  }
                />
              </Grid>
            </Tooltip>
          )}
          {awardLeadersData.company && (
            <Tooltip title={<Title />} placement="bottom">
              <Grid item {...awardContainerSize}>
                <Leaders
                  groupType={leaderboardGroupTypes.company}
                  groupTypeValue={user.companyName}
                  averageScore={awardLeadersData.company.averageScore}
                  leaders={awardLeadersData.company.leaders}
                  onViewAllClicked={() =>
                    handleOpenGroupLeadersModal(leaderboardGroupTypes.company)
                  }
                />
              </Grid>
            </Tooltip>
          )}
        </Grid>
      </Box>
      <Divider />
      <Box
        py={4}
        px={5}
        display="flex"
        flexDirection="column"
        alignItems={{ xs: "center", sm: "flex-start" }}
      >
        <Typography variant="h4">Available Awards</Typography>
        <Box display="flex" mt={2}>
          <Box
            display="flex"
            flexWrap="wrap"
            justifyContent={{ xs: "center", sm: "flex-start" }}
          >
            {availableAwards.map((award, index) => (
              <Box mr={{ xs: 0, sm: 5 }} mt={2} key={index}>
                <AwardButton
                  icon={award.icon}
                  awardColor={award.awardIcon.color}
                  customIcon={award.awardIcon.customIcon}
                  awardType={award.awardIcon.awardType}
                  awardTitle={award.title}
                  handleAwardClicked={() =>
                    handleOpenAvailableAwardModal(award)
                  }
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      {leadersModalInfo && (
        <LeadersModal
          open={groupLeadersModalOpen}
          onClose={handleCloseGroupLeadersModal}
          value={leadersModalInfo.value}
          leaders={leadersModalInfo.leaders}
        />
      )}
      {availableAwardModalAward && (
        <AwardDetailViewModal
          awardColor={availableAwardModalAward.awardIcon.color}
          awardType={availableAwardModalAward.awardIcon.awardType}
          awardTitle={availableAwardModalAward.title}
          customIcon={availableAwardModalAward.awardIcon.customIcon}
          description={availableAwardModalAward.description}
          criteria={availableAwardModalAward.criteria}
          leaders={availableAwardModalAward.leaders}
          open={availableAwardModalOpen}
          onClose={handleCloseAvailableAwardModal}
        />
      )}
    </Paper>
  );
}
